import { isDefined } from "~/shared/common";
import type { AgentMemory } from "~/shared/types";
import { agentMemoryComparator } from "~/utils/comparator";

import type { PiniaActionAdaptor, PiniaGetterAdaptor } from "../shared";
import type { DataStore } from ".";
import { removeFromSorted, updateInSorted } from "./utils";

export type State = {
  _duidsToAgentMemories: Map<string, AgentMemory>;
  _sortedAgentMemoryDuids: string[];
};

export const state: State = {
  _duidsToAgentMemories: new Map(),
  _sortedAgentMemoryDuids: [],
};

export type Getters = {
  getAgentMemoriesByAgentDuidOrdered: (agentDuid: string) => AgentMemory[];
  getAgentMemoryByDuid: (duid: string) => AgentMemory | undefined;
};

export type Actions = {
  deleteAgentMemory: (memory: AgentMemory, options?: { awaitBackend?: boolean }) => Promise<void>;
  $createOrUpdateAgentMemory: (memory: AgentMemory) => void;
  $deleteAgentMemory: (memory: AgentMemory) => void;
};

const getters: PiniaGetterAdaptor<Getters, DataStore> = {
  getAgentMemoriesByAgentDuidOrdered() {
    return (agentDuid) =>
      this._sortedAgentMemoryDuids
        .map((e) => this._duidsToAgentMemories.get(e))
        .filter(isDefined)
        .filter((e) => e.agentDuid === agentDuid);
  },
  getAgentMemoryByDuid() {
    return (duid) => this._duidsToAgentMemories.get(duid);
  },
};

const actions: PiniaActionAdaptor<Actions, DataStore> = {
  async deleteAgentMemory(memory, options = {}) {
    const { awaitBackend = false } = options;

    this.$deleteAgentMemory(memory);

    const backendAction = this.$backend.agentMemory.delete(memory.duid);
    if (awaitBackend) {
      await backendAction;
    }
  },
  $createOrUpdateAgentMemory(memory) {
    const current = this.getAgentMemoryByDuid(memory.duid);
    if (!current) {
      this._duidsToAgentMemories.set(memory.duid, memory);
    } else {
      Object.assign(current, memory);
    }
    updateInSorted(this._duidsToAgentMemories, this._sortedAgentMemoryDuids, memory.duid, agentMemoryComparator);
  },
  $deleteAgentMemory(memory) {
    this._duidsToAgentMemories.delete(memory.duid);
    removeFromSorted(this._sortedAgentMemoryDuids, memory.duid);
  },
};

export { actions, getters };
